import React from 'react';
import { Text, Link as ChakraLink } from '@chakra-ui/react';
import { Link as RouterLink } from '@tanstack/react-router';
import { useWarmRestaurantProfileChunk } from '@/hooks/useOpenRestaurantProfile';
import type { UserBasic, PostRestaurant, PostList } from '@/types/social';
import { analytics } from '@/lib/analytics';
import { AnalyticsEvents } from '@/lib/analytics/events';

interface RenderContentWithMentionsProps {
  content: string;
  mentions: UserBasic[];
  restaurants?: PostRestaurant[];
  lists?: PostList[];
  fontSize?: string;
  color?: string;
}

/**
 * Renders text content with mentions as clickable links (users, restaurants, lists)
 */
export function RenderContentWithMentions({
  content,
  mentions = [],
  restaurants = [],
  lists = [],
  fontSize = 'sm',
  color = 'black',
}: RenderContentWithMentionsProps) {
  // Restaurant mentions are plain router links (id + name only, cold data path);
  // warm the profile chunk so the tap does not wait for it.
  useWarmRestaurantProfileChunk(restaurants.length > 0);

  if (!content) {
    return null;
  }

  // Collect all mentions with their positions
  interface MentionItem {
    index: number;
    length: number;
    text: string;
    displayName: string;
    link: string;
    type: 'user' | 'restaurant' | 'list';
  }

  const allMentions: MentionItem[] = [];

  // Find user mentions (@)
  mentions.forEach((user) => {
    const mentionText = `@${user.name.replace(/ /g, '_')}`;
    let index = content.indexOf(mentionText);
    while (index !== -1) {
      allMentions.push({
        index,
        length: mentionText.length,
        text: mentionText,
        displayName: user.name,
        link: `/profile/${user.id}`,
        type: 'user',
      });
      index = content.indexOf(mentionText, index + 1);
    }
  });

  // Find restaurant mentions (+)
  restaurants.forEach((restaurant) => {
    const mentionText = `+${restaurant.name.replace(/ /g, '_')}`;
    let index = content.indexOf(mentionText);
    while (index !== -1) {
      allMentions.push({
        index,
        length: mentionText.length,
        text: mentionText,
        displayName: restaurant.name,
        link: `/restaurants/${restaurant.id}`,
        type: 'restaurant',
      });
      index = content.indexOf(mentionText, index + 1);
    }
  });

  // Find list mentions (#)
  lists.forEach((list) => {
    const mentionText = `#${list.name.replace(/ /g, '_')}`;
    let index = content.indexOf(mentionText);
    while (index !== -1) {
      allMentions.push({
        index,
        length: mentionText.length,
        text: mentionText,
        displayName: list.name,
        link: `/lists/${list.urlKey}`,
        type: 'list',
      });
      index = content.indexOf(mentionText, index + 1);
    }
  });

  // Sort mentions by position in content
  allMentions.sort((a, b) => a.index - b.index);

  // If no mentions found, return plain text
  if (allMentions.length === 0) {
    return (
      <Text fontSize={fontSize} color={color} whiteSpace="pre-wrap">
        {content}
      </Text>
    );
  }

  // Build the rendered parts
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let keyIndex = 0;

  allMentions.forEach((mention) => {
    // Add text before mention
    if (mention.index > lastIndex) {
      parts.push(
        <span key={`text-${keyIndex++}`}>
          {content.slice(lastIndex, mention.index)}
        </span>
      );
    }

    // Add mention as clickable link with clean name
    // Track click based on mention type
    const handleMentionClick = () => {
      if (mention.type === 'user') {
        analytics.capture(AnalyticsEvents.FEED_MEMBER_TAG_CLICKED, {
          member_id: mention.link.split('/').pop(),
          member_name: mention.displayName,
        });
      } else if (mention.type === 'restaurant') {
        analytics.capture(AnalyticsEvents.FEED_RESTAURANT_TAG_CLICKED, {
          restaurant_id: mention.link.split('/').pop(),
          restaurant_name: mention.displayName,
        });
      }
    };

    parts.push(
      <ChakraLink
        as={RouterLink}
        to={mention.link}
        key={`mention-${keyIndex++}`}
        color="blue.600"
        fontWeight="medium"
        _hover={{ textDecoration: 'underline' }}
        onClick={handleMentionClick}
      >
        {mention.displayName}
      </ChakraLink>
    );

    lastIndex = mention.index + mention.length;
  });

  // Add any remaining text
  if (lastIndex < content.length) {
    parts.push(
      <span key={`text-final-${keyIndex}`}>
        {content.slice(lastIndex)}
      </span>
    );
  }

  return (
    <Text fontSize={fontSize} color={color} whiteSpace="pre-wrap">
      {parts}
    </Text>
  );
}
